import { PriceLabel } from '@/components/features/shared/utils'
import { AlertWarning } from '@/components/ui/alertWarning'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import { BillingFrequency } from '@/domain/offerings/offering'
import { SubscriptionData } from '@/domain/subscriptions/subscriptions'
import { PlusCircle, X } from 'lucide-react'
import { endOfMonth, endOfYear, format } from 'date-fns'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'

interface CreateBillingPeriodModalProps {
  isOpen: boolean
  onClose: () => void
  subscription: SubscriptionData | undefined
  onCreateAndPay: (subId: string, periodStartDate: string, periodEndDate: string | undefined, amount: number) => void
  isPending: boolean
}

const MONTHS_ES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]

export const CreateBillingPeriodModal = ({
  isOpen,
  onClose,
  subscription,
  onCreateAndPay,
  isPending,
}: CreateBillingPeriodModalProps) => {
  const now = new Date()
  const initialYear = now.getFullYear()
  const initialMonth = now.getMonth() + 1

  const { control, reset, handleSubmit, watch } = useForm({
    defaultValues: {
      periodYear: initialYear,
      periodMonth: initialMonth,
      totalAmount: String(subscription?.subscriptionAmount ?? 0),
      enableMultiPeriod: false,
      endPeriodYear: initialYear,
      endPeriodMonth: initialMonth,
    },
  })

  const isMonthly = subscription?.billingFrequency === BillingFrequency.MONTHLY
  const periodYear = watch('periodYear')
  const periodMonth = watch('periodMonth')
  const enableMultiPeriod = watch('enableMultiPeriod')
  const endPeriodYear = watch('endPeriodYear')
  const endPeriodMonth = watch('endPeriodMonth')
  const totalAmount = watch('totalAmount')

  const periodStartDate = isMonthly
    ? `${periodYear}-${String(periodMonth).padStart(2, '0')}` as const
    : `${periodYear}` as const

  const periodLabel = isMonthly
    ? `${MONTHS_ES[periodMonth - 1]} ${periodYear}`
    : String(periodYear)

  const periodEndLabel = isMonthly
    ? `${MONTHS_ES[endPeriodMonth - 1]} ${endPeriodYear}`
    : String(endPeriodYear)

  useEffect(() => {
    if (subscription) {
      reset({
        periodYear: initialYear,
        periodMonth: initialMonth,
        totalAmount: String(subscription.subscriptionAmount),
        enableMultiPeriod: false,
        endPeriodYear: initialYear,
        endPeriodMonth: initialMonth,
      })
    }
  }, [subscription])

  if (!subscription) return null

  const onSubmit = (data: {
    periodYear: number
    periodMonth: number
    totalAmount: string
    enableMultiPeriod: boolean
    endPeriodYear: number
    endPeriodMonth: number
  }) => {
    const periodStartDate = isMonthly
      ? format(new Date(data.periodYear, data.periodMonth - 1, 1), 'yyyy-MM-dd')
      : format(new Date(data.periodYear, 0, 1), 'yyyy-MM-dd')
    const periodEndDate = isMonthly
      ? format(endOfMonth(new Date(data.endPeriodYear, data.endPeriodMonth - 1, 1)), 'yyyy-MM-dd')
      : format(endOfYear(new Date(data.endPeriodYear, 0, 1)), 'yyyy-MM-dd')
    onCreateAndPay(
      subscription.subscriptionId,
      periodStartDate,
      data.enableMultiPeriod ? periodEndDate : undefined,
      parseFloat(data.totalAmount)
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Agregar nuevo período</DialogTitle>
          <DialogDescription>
            Agrega un nuevo período de facturación de forma manual.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 my-4">
          <div>
            <label className="text-sm font-medium mb-1 block">
              Período
            </label>
            <div className="flex gap-2">
              {isMonthly && (
                <Controller
                  name="periodMonth"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <select
                      className="flex-1 border rounded-lg px-3 py-2 text-sm bg-background"
                      value={value}
                      onChange={e => onChange(Number(e.target.value))}
                      disabled={isPending}
                    >
                      {MONTHS_ES.map((month, idx) => (
                        <option key={idx + 1} value={idx + 1}>{month}</option>
                      ))}
                    </select>
                  )}
                />
              )}
              <Controller
                name="periodYear"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <select
                    className="flex-1 border rounded-lg px-3 py-2 text-sm bg-background"
                    value={value}
                    onChange={e => onChange(Number(e.target.value))}
                    disabled={isPending}
                  >
                    {Array.from({ length: now.getFullYear() + 3 - 2000 + 1 }, (_, i) => String(2000 + i)).map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                )}
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Controller
              name="enableMultiPeriod"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Switch
                  checked={value}
                  onCheckedChange={onChange}
                  disabled={isPending}
                />
              )}
            />
            <label className="text-sm font-medium">
              Múltiples períodos
            </label>
          </div>

          {enableMultiPeriod && (
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium mb-1 block">
                  Último período
                </label>
                <div className="flex gap-2">
                  {isMonthly && (
                    <Controller
                      name="endPeriodMonth"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <select
                          className="flex-1 border rounded-lg px-3 py-2 text-sm bg-background"
                          value={value}
                          onChange={e => onChange(Number(e.target.value))}
                          disabled={isPending}
                        >
                          {MONTHS_ES.map((month, idx) => (
                            <option key={idx + 1} value={idx + 1}>{month}</option>
                          ))}
                        </select>
                      )}
                    />
                  )}
                  <Controller
                    name="endPeriodYear"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <select
                        className="flex-1 border rounded-lg px-3 py-2 text-sm bg-background"
                        value={value}
                        onChange={e => onChange(Number(e.target.value))}
                        disabled={isPending}
                      >
                        {Array.from({ length: now.getFullYear() + 3 - 2000 + 1 }, (_, i) => String(2000 + i)).map(y => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    )}
                  />
                </div>
              </div>

              <AlertWarning>Los períodos serán consecutivos desde el primer período hasta el último período inclusive.</AlertWarning>
            </div>
          )}

          <div>
            <label className="text-sm font-medium mb-1 block">
              Monto
            </label>
            <Controller
              name="totalAmount"
              control={control}
              render={({ field: { onChange, value } }) => (
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-background"
                  value={value}
                  onChange={e => onChange(e.target.value)}
                  disabled={isPending}
                />
              )}
            />
          </div>

          <div className="bg-muted/30 p-3 rounded-lg border">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Monto del período:</span>
              <PriceLabel price={parseFloat(totalAmount) || subscription.totalAmount} />
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-xs text-muted-foreground">Primer período:</span>
              <span className="text-xs font-mono">{periodLabel}</span>
            </div>
            {enableMultiPeriod && (
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-muted-foreground">Último período:</span>
                <span className="text-xs font-mono">{periodEndLabel}</span>
              </div>
            )}
          </div>

          <DialogFooter className="mt-4">
            <div className='flex justify-between w-full'>
              <Button variant="outline" type="button" onClick={onClose} disabled={isPending}>
                <X className="h-3.5 w-3.5 mr-1.5" />
                Cancelar
              </Button>
              <Button
                variant="info"
                type="submit"
                disabled={!periodStartDate || !totalAmount || isPending}
              >
                <PlusCircle className="h-3.5 w-3.5 mr-1.5" />
                Crear período
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
